import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api/client';
import { dayBounds } from '@/lib/store-time';
import type {
  DiscountsReport,
  ExcludedItem,
  PosDiscount,
  PosDiscountInput,
} from './types';

/**
 * POS discounts — the ones a cashier can apply at the till — and the report of
 * where they were used.
 *
 * Both halves read the same `pos/discounts` list, which is why they are one
 * feature: the report's filter is a multi-select of exactly the records the
 * other tab maintains.
 */

const KEY = ['pos-discounts'] as const;

export function usePosDiscounts() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<PosDiscount[]>('pos/discounts'),
    select: (data) => data ?? [],
    staleTime: 60_000,
  });
}

/**
 * The items a discount is withheld from.
 *
 * The list endpoint already inlines `excludedItems`, so this is only for
 * refreshing one discount's exclusions after an edit without refetching every
 * discount and its whole item set.
 */
export function useExcludedItems(discountId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'excluded', discountId],
    queryFn: () =>
      get<ExcludedItem[]>(`pos/discounts/items/${discountId}`),
    select: (data) => data ?? [],
    enabled: discountId !== null,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/** Create and update are one POST, decided by `id`. */
export function useSavePosDiscount() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (values: PosDiscountInput) =>
      post<unknown>('pos/discounts', values),
    onSuccess: invalidate,
  });
}

export function useDeletePosDiscount() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) => del<unknown>(`pos/discounts/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * Add or drop one excluded item.
 *
 * One endpoint for both directions, told apart by `IsDelete` — so the caller
 * says what it wants rather than picking a route.
 */
export function useSetExcludedItem(discountId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ itemId, remove }: { itemId: string; remove: boolean }) =>
      post<unknown>('pos/discounts/items', {
        PosDiscountId: discountId,
        ItemId: itemId,
        IsDelete: remove,
      }),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------- report

export interface UsageQuery {
  start: string;
  end: string;
  lifetime: boolean;
  discountIds: string[];
  businessId: string | null;
}

export function useDiscountUsage(params: UsageQuery, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'usage', params],
    queryFn: () => {
      const { startDate, endDate } = dayBounds(params.start, params.end);
      return post<DiscountsReport>(
        // The per-store variant is a path segment, not a field.
        `analytics/reporting/discounts/orders${
          params.businessId ? `/${params.businessId}` : ''
        }`,
        {
          StartDate: startDate,
          EndDate: endDate,
          Lifetime: params.lifetime,
          DiscountIds: params.discountIds,
        }
      );
    },
    enabled,
    staleTime: 60_000,
  });
}
